import { useAsset } from 'core/logic/asset/asset.hook'
import { useParams } from 'react-router-dom'
import AssetDetail, { AssetDetailViewProps } from './AssetDetail.view'
import { ComponentProps, useMemo } from 'react'
import {
  AssetType,
  AssetWithBalance,
  SaleWithImages,
} from 'core/logic/asset/asset.types'
import useCustomHistory from '@onepercentio/one-ui/hooks/useCustomHistory'
import ROUTES, { TO_MARKETPLACE_OFFER_BUY } from 'core/modules/router'
import { i18n, t } from 'translate/i18n'
import { useUpToDateMyCollectionItems } from 'context/MyCollection'
import useQuery from '@onepercentio/one-ui/hooks/utility/useQuery'
import { useSaleAvailableOnMarketplace } from 'context/Marketplace'
import SectionLoader from 'openspace/components/SectionLoader'
import { useMarketplaceDigitalTwinStorage } from 'pages/Authenticated/Marketplace/CreateOffer/OfferType/DigitalTwin/DigitalTwin.hooks'
import { useAssetsBy } from 'context/Asset'
import Button from '@onepercentio/one-ui/components/Button'

const AssetDetailLogic = ({ marketplace }: { marketplace: boolean }) => {
  const history = useCustomHistory()
  const { assetId } = useParams<{ assetId: string }>()
  const { ownedItems } = useUpToDateMyCollectionItems()
  const singleAsset = useAssetsBy('id', [assetId]).assets[0]
  const onBackClickHandler = () =>
    history.goBackWithFallback(ROUTES.unauthenticated.homepage)
  const asset = useMemo<AssetWithBalance | AssetType | null>(() => {
    return ownedItems?.find((a) => a.id === assetId) || singleAsset || null
  }, [ownedItems, assetId, singleAsset])
  const intl = asset?.[i18n.language] || asset!
  const offerId = useQuery<{ offerId: string }>().get('offerId')

  return (
    asset &&
    (marketplace ? (
      offerId ? (
        <SingleMarketplaceOfferWrapper
          asset={asset}
          title={intl.name}
          description={intl.description}
          features={intl.features}
          assetUrl={asset?.media.imageUrl}
          onBack={onBackClickHandler}
          ipfs={asset?.ipfs.jsonFile}
          marketplace={marketplace}
          onBuyItem={(s) =>
            history.push(TO_MARKETPLACE_OFFER_BUY(asset!.id, s.offerId))
          }
          offerId={offerId}
        />
      ) : (
        <AssetMarketplaceSummaryWrapper
          asset={asset}
          title={intl.name}
          description={intl.description}
          features={intl.features}
          assetUrl={asset?.media.imageUrl}
          onBack={onBackClickHandler}
          ipfs={asset?.ipfs.jsonFile}
          marketplace={marketplace}
        />
      )
    ) : (
      <AssetDetailWrapper
        asset={asset}
        title={intl.name}
        description={intl.description}
        features={intl.features}
        assetUrl={asset?.media.imageUrl}
        onBack={onBackClickHandler}
        ipfs={asset?.ipfs.jsonFile}
        marketplace={marketplace}
      />
    ))
  )
}

export function AssetDetailWrapper(
  props: ComponentProps<typeof AssetDetail> & { marketplace: false }
) {
  return <AssetDetail {...props} />
}

export function AssetMarketplaceSummaryWrapper(
  props: Omit<
    ComponentProps<typeof AssetDetail> & { marketplace: true },
    'onBuyItem' | 'sales'
  >
) {
  return <AssetDetail {...props} sales={[]} onBuyItem={() => {}} />
}

export function SingleMarketplaceOfferWrapper(
  props: Omit<
    ComponentProps<typeof AssetDetail> & { marketplace: true },
    'sales'
  > & {
    offerId: string
  }
) {
  const relatedSale = useSaleAvailableOnMarketplace(props.offerId)
  const {
    initialImages: saleImages,
    error,
    retry,
  } = useMarketplaceDigitalTwinStorage(
    props.asset.galleryId!,
    relatedSale?.tokenId as number,
    relatedSale?.user!
  )
  const saleWithImages = useMemo<SaleWithImages | undefined>(
    () =>
      relatedSale && saleImages
        ? { ...relatedSale, images: saleImages.map((a) => a.objUrl) }
        : undefined,
    [relatedSale, saleImages]
  )
  if (!saleWithImages)
    return (
      <>
        <SectionLoader />
        {error && <Button onClick={retry}>{t('generic.retryBtn')}</Button>}
      </>
    )
  return <AssetDetail {...props} sales={[saleWithImages]} />
}

export default AssetDetailLogic
